/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash } from '@saleshandy/icons';
import Checkbox from '../../../../shared/design-system/components/atoms/checkbox';
import Input from '../../../../shared/design-system/components/input';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';

export interface MCQOption {
  id: number;
  label: string;
  isCustom: boolean;
}

interface AccordionItemProps {
  id: number;
  title: string;
  options: MCQOption[];
  selectedOptions: MCQOption[];
  onSelectionChange: (options: MCQOption[]) => void;
  expandedId: number | null;
  setExpandedId: (id: number | null) => void;
  fieldErrors: Record<number, string>;
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  title,
  options,
  selectedOptions,
  onSelectionChange,
  expandedId,
  setExpandedId,
  fieldErrors,
  setFieldErrors,
}) => {
  const isExpanded = expandedId === id;
  const [customOptions, setCustomOptions] = useState<MCQOption[]>([]);
  const [editingCustomId, setEditingCustomId] = useState<number | null>(null);

  const allOptions = useMemo(() => [...options, ...customOptions], [
    options,
    customOptions,
  ]);

  const hasErrors = useMemo(() => Object.keys(fieldErrors)?.length > 0, [
    fieldErrors,
  ]);

  const toggleExpand = useCallback(() => {
    setExpandedId(isExpanded ? null : id);
  }, [isExpanded, id, setExpandedId]);

  const handleCheckboxChange = useCallback(
    (option: MCQOption, checked: boolean) => {
      const alreadySelected = selectedOptions?.some(
        (o) => o?.id === option?.id,
      );

      if (checked && !alreadySelected) {
        onSelectionChange([...selectedOptions, option]);
      } else if (!checked && alreadySelected) {
        onSelectionChange(selectedOptions.filter((o) => o?.id !== option?.id));
      }
    },
    [selectedOptions, onSelectionChange],
  );

  const handleAddCustom = useCallback(() => {
    const newId = Date.now();
    const newOption: MCQOption = {
      id: newId,
      label: '',
      isCustom: true,
    };
    setCustomOptions((prev) => [...prev, newOption]);
    onSelectionChange([...selectedOptions, newOption]);
    setEditingCustomId(newId);
  }, [selectedOptions, onSelectionChange]);

  const handleDeleteCustom = useCallback(
    (idToDelete: number) => {
      setCustomOptions((prev) => prev?.filter((opt) => opt?.id !== idToDelete));
      onSelectionChange(selectedOptions?.filter((o) => o?.id !== idToDelete));
      setFieldErrors((prev) => {
        const { [idToDelete]: _, ...rest } = prev;
        return rest;
      });
    },
    [selectedOptions, onSelectionChange, setFieldErrors],
  );

  const handleCustomLabelChange = useCallback(
    (optId: number, value: string) => {
      setCustomOptions((prev) =>
        prev?.map((o) => (o?.id === optId ? { ...o, label: value } : o)),
      );

      const updatedSelections = selectedOptions.map((opt) =>
        opt?.id === optId ? { ...opt, label: value } : opt,
      );
      onSelectionChange(updatedSelections);

      const trimmed = value?.trim();
      if (
        trimmed?.length > 0 &&
        (trimmed?.length < 1 || trimmed?.length > 100)
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          [optId]: 'Custom option must be between 1 and 100 characters.',
        }));
      } else {
        setFieldErrors((prev) => {
          const { [optId]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [setCustomOptions, setFieldErrors, selectedOptions, onSelectionChange],
  );

  const handleCustomBlur = useCallback(
    (opt: MCQOption) => {
      const trimmed = opt?.label?.trim();
      if (trimmed === '') {
        handleDeleteCustom(opt?.id);
      } else if (trimmed?.length >= 10 && trimmed?.length <= 100) {
        setEditingCustomId(null);
      }
    },
    [handleDeleteCustom],
  );
  return (
    <div className="accordion-item">
      <div
        className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
        {...(!hasErrors ? accessibleOnClick(toggleExpand) : {})}
      >
        <span>{title}</span>
        {isExpanded ? <ChevronUp /> : <ChevronDown />}
      </div>

      {isExpanded && (
        <div className="accordion-content">
          {allOptions?.map((opt) => {
            const isSelected = selectedOptions?.some((o) => o?.id === opt?.id);
            const isEditing = editingCustomId === opt?.id;
            const error = fieldErrors[opt?.id];

            return (
              <div className="option-block">
                <div
                  key={opt?.id}
                  className={`option-row ${isEditing ? 'isEditing' : ''} ${
                    error ? 'has-error' : ''
                  }`}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={(checked) => handleCheckboxChange(opt, checked)}
                  />
                  {isEditing ? (
                    <Input
                      autoFocus
                      value={opt?.label}
                      onChange={(val) => handleCustomLabelChange(opt?.id, val)}
                      onBlur={() => handleCustomBlur(opt)}
                      placeholder="Name your primary goal"
                    />
                  ) : (
                    <span className="option-label">{opt?.label}</span>
                  )}
                  {opt?.isCustom && (
                    <div className="delete-icon">
                      <Trash
                        onClick={() => handleDeleteCustom(opt?.id)}
                        color="#9CA3AF"
                      />
                    </div>
                  )}
                </div>
                {error && <p className="field-error">{error}</p>}
              </div>
            );
          })}

          <div
            {...(customOptions?.length < 3 && !hasErrors
              ? accessibleOnClick(handleAddCustom)
              : {})}
            className={`add-button ${
              customOptions?.length >= 3 || hasErrors ? 'disabled' : ''
            }`}
          >
            <Plus width={16} height={16} />
            Other
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(AccordionItem);
